/**
 * FullFinancialAuditReport — RESTRICTED TO JOHAN (johan@beaversden.co.za) ONLY
 * Modal report displaying every financial movement record for a single user
 * across purchases, payments, commissions, balances, conversions, withdrawals,
 * transfers, prizes, and admin audit logs. Highlights reconciliation breakpoints.
 * v4.7.41 — forensic mode: all linked rows are surfaced regardless of status;
 *           summary calculations use explicit gross_/net_ naming; no row is
 *           silently excluded from the audit.
 * v4.7.52 — bank-statement redesign: the per-section flat dump is replaced by
 *           a 5-tab layout (Statement / Shares / Commissions Earned /
 *           Commissions Generated / Referrer) with a chronological ledger and
 *           running balance starting from 0 at user signup. The forensic
 *           reconciliation block + query errors move into a collapsible
 *           Diagnostics drawer so the bank-statement view stays clean. A new
 *           "Client Statement" PDF button produces a branded, UUID-free PDF
 *           Johan can send to clients; XLSX + Forensic PDF buttons are kept
 *           for internal investigation.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ensureSessionHeaders } from '../../lib/serverSession';
import { FullFinancialAuditTabs } from './FullFinancialAuditTabs';
import { downloadAuditXlsx, downloadAuditPdf, downloadClientStatementPdf } from '../../lib/auditExport';
import { logAction } from '../../lib/personalActivityLogger';

interface Props {
  userId: number;
  username: string;
  onClose: () => void;
}

interface AuditPayload {
  success: boolean;
  target_user_id: number;
  generated_at: string;
  disclaimer?: string;
  profile: any;
  kyc: any;
  share_purchases: any;
  crypto_payments: any;
  yoco_payments: any;
  commissions_earned: any;
  commissions_paid_to_others: any;
  commission_balance: any;
  commission_conversions: any;
  withdrawals: any;
  share_transfers: any;
  competition_prizes: any;
  admin_audit_logs: any[];
  referrer?: any;
  reconciliation: any;
  query_errors: string[];
}

const fmt = (n: any, d = 2) => (typeof n === 'number' ? n : parseFloat(n ?? 0) || 0).toFixed(d);
const dt = (s: any) => (s ? new Date(s).toLocaleString() : '—');

const card: React.CSSProperties = { backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px', padding: '12px', marginBottom: '12px' };
const h: React.CSSProperties = { color: '#F59E0B', fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' };
const lbl: React.CSSProperties = { color: '#9CA3AF', fontSize: '11px' };
const val: React.CSSProperties = { color: '#FFFFFF', fontSize: '13px', fontWeight: 600 };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '11px', color: '#E5E7EB' };
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', backgroundColor: '#1F2937', color: '#F3F4F6', borderBottom: '1px solid #374151', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #1F2937', verticalAlign: 'top' };

function StatusPill({ status }: { status: string }) {
  const s = String(status || '').toLowerCase();
  const colour = s === 'approved' || s === 'active' || s === 'completed' || s === 'paid' ? '#065F46' : s.startsWith('pending') || s === 'processing' ? '#92400E' : s === 'rejected' || s === 'cancelled' || s === 'reversed' ? '#991B1B' : '#374151';
  return <span style={{ backgroundColor: colour, color: '#FFF', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>{status || '—'}</span>;
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div style={card}>
      <h3 style={h}>{title}{typeof count === 'number' ? ` (${count})` : ''}</h3>
      {children}
    </div>
  );
}

export function FullFinancialAuditReport({ userId, username, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AuditPayload | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const headers = await ensureSessionHeaders();
      const r = await fetch('/api/admin/full-financial-audit', { method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify({ targetUserId: userId }) });
      const j = await r.json();
      if (!r.ok || !j?.success) { setError(j?.error || `Request failed (${r.status})`); setData(null); }
      else setData(j as AuditPayload);
    } catch (e: any) { setError(e?.message || 'Network error'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // v4.7.53 — Personal Activity: record which user Johan is auditing
  useEffect(() => {
    logAction('user_edit', 'open_full_financial_audit', {
      target_type: 'user',
      target_id: String(userId),
      target_label: username || `User ${userId}`
    });
  }, [userId, username]);

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ backgroundColor: '#0F172A', border: '2px solid #F59E0B', borderRadius: '12px', maxWidth: '1280px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #374151', paddingBottom: '12px' }}>
          <div>
            <h2 style={{ color: '#F59E0B', fontSize: '20px', margin: 0, fontWeight: 700 }}>💰 Full User Financial Movement Audit</h2>
            <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '4px 0 0 0' }}>User #{userId} — {username}{data ? ` — generated ${dt(data.generated_at)}` : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={load} disabled={loading} style={{ backgroundColor: '#3B82F6', color: '#FFF', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600 }}>↻ Reload</button>
            <button onClick={() => data && downloadClientStatementPdf(data)} disabled={!data || loading} title="Download a clean, branded Client Statement PDF (no internal UUIDs / diagnostics) suitable for sending to the client." style={{ backgroundColor: !data || loading ? '#374151' : '#F59E0B', color: '#111827', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: !data || loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 700 }}>📑 Client Statement</button>
            <button onClick={() => data && downloadAuditXlsx(data)} disabled={!data || loading} title="Download full forensic audit as Excel workbook (one sheet per section). Internal use only." style={{ backgroundColor: !data || loading ? '#374151' : '#10B981', color: '#FFF', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: !data || loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600 }}>📊 XLSX</button>
            <button onClick={() => data && downloadAuditPdf(data)} disabled={!data || loading} title="Download full forensic audit PDF (includes UUIDs, diagnostic codes). Internal use only." style={{ backgroundColor: !data || loading ? '#374151' : '#DC2626', color: '#FFF', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: !data || loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600 }}>📄 Forensic PDF</button>
            <button onClick={onClose} style={{ backgroundColor: '#374151', color: '#FFF', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>✕ Close</button>
          </div>
        </div>

        {loading && <div style={{ color: '#FFF', padding: '32px', textAlign: 'center' }}>Loading full financial audit…</div>}
        {error && <div style={{ color: '#FCA5A5', backgroundColor: '#7F1D1D', padding: '12px', borderRadius: '6px' }}>Error: {error}</div>}

        {data && !loading && (
          <div>
            {data.disclaimer && (
              <div style={{ backgroundColor: '#1E3A8A', border: '1px solid #3B82F6', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', color: '#DBEAFE', fontSize: '12px', lineHeight: 1.5 }}>
                <strong style={{ color: '#FFF' }}>🔍 Forensic disclaimer:</strong> {data.disclaimer}
              </div>
            )}
            <Section title="👤 Profile + KYC">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                <div><div style={lbl}>ID</div><div style={val}>{data.profile?.id}</div></div>
                <div><div style={lbl}>Username</div><div style={val}>{data.profile?.username || '—'}</div></div>
                <div><div style={lbl}>Email</div><div style={val}>{data.profile?.email || '—'}</div></div>
                <div><div style={lbl}>Full Name</div><div style={val}>{data.profile?.full_name || '—'}</div></div>
                <div><div style={lbl}>Country</div><div style={val}>{data.profile?.country_name || data.profile?.country_of_residence || '—'}</div></div>
                <div><div style={lbl}>Account Status</div><div style={val}>{data.profile?.account_status || (data.profile?.is_active ? 'active' : 'inactive')}</div></div>
                <div><div style={lbl}>Created</div><div style={val}>{dt(data.profile?.created_at)}</div></div>
                <div><div style={lbl}>Last Login</div><div style={val}>{dt(data.profile?.last_login_at)}</div></div>
                <div><div style={lbl}>KYC Status</div><div style={val}><StatusPill status={data.kyc?.kyc_status || 'none'} /></div></div>
                <div><div style={lbl}>KYC Completed</div><div style={val}>{dt(data.kyc?.kyc_completed_at)}</div></div>
              </div>
            </Section>

            <FullFinancialAuditTabs data={data} />

            <div style={{ marginTop: '16px', borderTop: '1px solid #374151', paddingTop: '12px' }}>
              <button onClick={() => setShowDiagnostics(s => !s)} style={{ backgroundColor: '#1F2937', color: '#9CA3AF', border: '1px solid #374151', borderRadius: '6px', padding: '8px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }} title="Internal forensic diagnostics. Not shown in the Client Statement PDF.">
                {showDiagnostics ? '▾' : '▸'} Diagnostics (reconciliation · breakpoints · query errors)
                {data.reconciliation?.breakpoint_count ? <span style={{ marginLeft: '8px', color: '#FCA5A5' }}>· {data.reconciliation.breakpoint_count} breakpoint(s)</span> : null}
              </button>
              {showDiagnostics && (
                <div style={{ marginTop: '10px' }}>
                  <Section title="🚨 Reconciliation Breakpoints" count={data.reconciliation?.breakpoint_count}>
                    {(!data.reconciliation?.breakpoints || data.reconciliation.breakpoints.length === 0) ? (
                      <p style={{ color: '#86EFAC', fontSize: '13px', margin: 0 }}>✅ No breakpoints detected in the financial flow.</p>
                    ) : (
                      <table style={tableStyle}><thead><tr><th style={th}>Severity</th><th style={th}>Category</th><th style={th}>Reference</th><th style={th}>Amount</th><th style={th}>When</th><th style={th}>Message</th></tr></thead><tbody>
                        {data.reconciliation.breakpoints.map((b: any, i: number) => (
                          <tr key={i} style={{ backgroundColor: b.severity === 'critical' ? 'rgba(239,68,68,0.1)' : b.severity === 'warning' ? 'rgba(245,158,11,0.08)' : 'rgba(59,130,246,0.06)' }}>
                            <td style={td}><StatusPill status={b.severity} /></td>
                            <td style={td}>{b.category}</td>
                            <td style={{ ...td, fontFamily: 'monospace' }}>{b.purchase_id || b.withdrawal_id || b.transfer_id || b.row_id || '—'}</td>
                            <td style={td}>{b.amount != null ? `$${fmt(b.amount)}` : b.diff_usdt != null ? `Δ $${fmt(b.diff_usdt)}` : b.usdt_commission != null ? `$${fmt(b.usdt_commission)}` : '—'}</td>
                            <td style={td}>{dt(b.created_at)}</td>
                            <td style={td}>{b.message}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    )}
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF', display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
                      <span>Recorded earned USDT: <strong style={{ color: '#FFF' }}>${fmt(data.reconciliation.recorded_total_earned_usdt)}</strong></span>
                      <span>Gross all commission rows: <strong style={{ color: '#FFF' }}>${fmt(data.reconciliation.gross_usdt_all_commission_records)}</strong></span>
                      <span>Net (excl. rejected/cancelled/reversed/failed): <strong style={{ color: '#FFF' }}>${fmt(data.reconciliation.net_usdt_earned_excluding_rejected_cancelled_reversed_failed)}</strong></span>
                      <span>Diff (recorded − net): <strong style={{ color: Math.abs(data.reconciliation.usdt_earned_diff_recorded_minus_net || 0) > 0.01 ? '#FCA5A5' : '#86EFAC' }}>${fmt(data.reconciliation.usdt_earned_diff_recorded_minus_net)}</strong></span>
                      <span>Has upline: <strong style={{ color: '#FFF' }}>{data.reconciliation.has_upline ? 'YES' : 'NO'}</strong></span>
                    </div>
                  </Section>
                  {data.query_errors && data.query_errors.length > 0 && (
                    <Section title="⚠️ Partial Query Errors">
                      <ul style={{ color: '#FCA5A5', fontSize: '11px', margin: 0, paddingLeft: '20px' }}>{data.query_errors.map((e: string, i: number) => <li key={i}>{e}</li>)}</ul>
                    </Section>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
